import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, map, of, tap } from 'rxjs';
import { ConfigService } from './config.service';
import { HttpService } from './http.service'
import { AppModule, AppSettingsDto, ModelChoiceDto, ModuleSettingsDto } from './settings.dto';

const API_EXT = '/settings/'

/**
 * Réglages de la compagnie, chargés en un appel.
 *
 * La charge est structurée par module : il n'y a plus de bloc « chat » portant
 * les drapeaux des autres, ni de second appel pour la liste des moteurs.
 */
@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  constructor(
    private http: HttpClient,
    private httpService: HttpService,
    private configService: ConfigService
  ) { }

  private get api_url(): string {
    const base = this.configService.getApiBaseUrl(); // OK si config chargée
    return base + API_EXT;
  }

  private settingsSubject = new BehaviorSubject<AppSettingsDto | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  // ─── Observables publics ───────────────────────────────────

  settings$ = this.settingsSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  error$ = this.errorSubject.asObservable();

  /** Les moteurs proposables à l'utilisateur. */
  models$: Observable<ModelChoiceDto[]> = this.settings$.pipe(
    map(settings => settings?.models ?? [])
  );

  // ─── Lecture d'un module ───────────────────────────────────

  moduleSettings$(module: AppModule): Observable<ModuleSettingsDto> {
    return this.settings$.pipe(map(settings => settings?.modules?.[module] ?? {}));
  }

  /**
   * Un réglage précis. `fallback` est rendu tant que les réglages ne sont pas
   * chargés, ou si le module ne porte pas cette clé.
   */
  setting$<T extends number | string>(module: AppModule, key: string, fallback: T): Observable<T> {
    return this.moduleSettings$(module).pipe(
      map(settings => (settings[key] ?? fallback) as T)
    );
  }

  setting<T extends number | string>(module: AppModule, key: string, fallback: T): T {
    const settings = this.currentSettings?.modules?.[module];
    return (settings?.[key] ?? fallback) as T;
  }

  /**
   * Activation d'un module. Tant que rien n'est chargé, il est tenu pour
   * ouvert : une navigation qui clignote au chargement est pire qu'une entrée
   * de trop pendant une seconde.
   */
  moduleEnabled$(module: AppModule): Observable<boolean> {
    return this.settings$.pipe(
      map(settings => {
        const known = settings?.modules?.[module];
        return known === undefined || Number(known['status']) === 1;
      })
    );
  }

  isModuleEnabled(module: AppModule): boolean {
    const known = this.currentSettings?.modules?.[module];
    return known === undefined || Number(known['status']) === 1;
  }

  // ─── Raccourcis lisibles ───────────────────────────────────

  /** Nombre de dossiers autorisés ; 0 quand la société n'impose pas de limite. */
  moduleDossierMaxFolder$ = this.setting$('rag', 'max_folder', 0);

  /** Nombre de fichiers autorisés par dossier ; 0 quand il n'y a pas de limite. */
  moduleDossierMaxFilesByFolder$ = this.setting$('rag', 'max_files_by_folder', 0);

  webSearchEnabled$ = this.setting$('conversation', 'web_search_status', 0).pipe(
    map(value => Number(value) === 1)
  );

  webSearchDefault$ = this.setting$('conversation', 'web_search_default', 0).pipe(
    map(value => Number(value) as 0 | 1 | 2)
  );

  // ─── Chargement / Refresh ──────────────────────────────────

  loadSettings(): Observable<AppSettingsDto | null> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.http.get<AppSettingsDto>(this.api_url).pipe(
      tap(settings => {
        this.settingsSubject.next(settings);
        this.loadingSubject.next(false);
      }),
      catchError(err => {
        console.error('[SettingsService] Erreur chargement settings:', err);
        this.errorSubject.next('Impossible de charger les paramètres');
        this.loadingSubject.next(false);
        // On garde les anciens settings s'ils existent
        return of(this.settingsSubject.getValue());
      })
    );
  }

  get currentSettings(): AppSettingsDto | null {
    return this.settingsSubject.getValue();
  }

  get models(): ModelChoiceDto[] {
    return this.currentSettings?.models ?? [];
  }

  /**
   * Fenêtre de contexte d'un moteur, en jetons ; 0 quand le moteur est
   * inconnu de la société ou que sa fenêtre n'est pas renseignée.
   */
  contextWindowOf(reference: string | null | undefined): number {
    if (!reference) {
      return 0;
    }

    const model = this.models.find(item => item.reference === reference);

    return Number(model?.max_token) || 0;
  }

  /**
   * Fenêtre de contexte du moteur qui traitera la demande d'un module.
   *
   * `reference` est le choix de l'utilisateur, qui vaut « default » tant
   * qu'il n'a rien choisi : on retombe alors sur le moteur paramétré pour le
   * module, celui que le serveur retiendra.
   */
  contextWindow(module: AppModule, reference?: string | null): number {
    return (
      this.contextWindowOf(reference) ||
      this.contextWindowOf(this.setting(module, 'default_gai', ''))
    );
  }

  /** Appelé à la déconnexion : les réglages sont propres à la société. */
  clear(): void {
    this.settingsSubject.next(null);
    this.errorSubject.next(null);
  }
}
